import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { firebaseApp } from '../config/firebase';
import { UpstoxAuthService } from '../services/upstoxAuth';
import { upstoxConfig } from '../config/upstox';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  upstoxToken: string;
  setUpstoxToken: (token: string) => void;
  connectUpstox: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  upstoxToken: '',
  setUpstoxToken: () => {},
  connectUpstox: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [upstoxToken, setUpstoxToken] = useState<string>(() => {
    // Initialize from localStorage
    return localStorage.getItem('upstox_token') || '';
  });

  // Update localStorage when token changes
  const updateUpstoxToken = (token: string) => {
    if (token) {
      localStorage.setItem('upstox_token', token);
    } else {
      localStorage.removeItem('upstox_token');
    }
    setUpstoxToken(token);
  };

  const connectUpstox = () => {
    const authService = new UpstoxAuthService(upstoxConfig);
    authService.initiateAuth();
  };

  useEffect(() => {
    try {
      const auth = getAuth(firebaseApp);
      return onAuthStateChanged(auth, (user) => {
        setUser(user);
        setLoading(false);
        // Clear upstox token on logout
        if (!user) {
          updateUpstoxToken('');
        }
      });
    } catch (error) {
      console.error('Firebase auth error:', error);
      setLoading(false);
    }
  }, []);

  console.log('Auth Token:', upstoxToken); // Added log

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      upstoxToken, 
      setUpstoxToken: updateUpstoxToken,
      connectUpstox, // Added connectUpstox
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
